import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingButton } from '@/components/ui/loading-button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const ForgotPassword = ({ onBackToLogin }) => {
  const [step, setStep] = useState(1); // 1: Email, 2: Code et nouveau mot de passe
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [devCode, setDevCode] = useState(''); // Pour afficher le code en développement

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: email
      });

      toast.success('Code de réinitialisation envoyé !');
      
      // En mode développement, afficher le code
      if (response.data.dev_code) {
        setDevCode(response.data.dev_code);
        toast.info(`Code de développement: ${response.data.dev_code}`, {
          duration: 10000
        });
      }
      
      setStep(2);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi du code';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        email: email,
        reset_code: resetCode,
        new_password: newPassword
      });

      toast.success('Mot de passe réinitialisé avec succès !');
      
      // Retourner à la page de connexion après un délai
      setTimeout(() => {
        onBackToLogin();
      }, 2000);

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la réinitialisation';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackToLogin}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <CardTitle className="text-2xl">
                {step === 1 ? 'Mot de passe oublié' : 'Nouveau mot de passe'}
              </CardTitle>
              <CardDescription>
                {step === 1 
                  ? 'Entrez votre email pour recevoir un code de réinitialisation'
                  : 'Entrez le code reçu et votre nouveau mot de passe'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        {step === 1 ? (
          <form onSubmit={handleEmailSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Adresse email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter>
              <LoadingButton 
                type="submit" 
                className="w-full"
                disabled={!email}
                loading={loading}
                loadingText="Envoi en cours..."
              >
                Envoyer le code
              </LoadingButton>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleResetSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resetCode">Code de réinitialisation</Label>
                <Input
                  id="resetCode"
                  type="text"
                  placeholder="123456"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                />
                <p className="text-xs text-gray-600">
                  Vérifiez votre email pour le code à 6 chiffres
                </p>
                {devCode && (
                  <p className="text-xs text-blue-600 font-mono">
                    Code de dev: {devCode}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Nouveau mot de passe"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-9 pr-9"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirmer le mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-9 pr-9"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-2">
              <LoadingButton 
                type="submit" 
                className="w-full"
                disabled={!resetCode || !newPassword || !confirmPassword}
                loading={loading}
                loadingText="Réinitialisation..."
              >
                Réinitialiser le mot de passe
              </LoadingButton>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep(1);
                  setResetCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setDevCode('');
                }}
                className="text-xs"
              >
                Renvoyer un code
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPassword;